import { CentralConfig } from './CentralConfig';
import { PushConfig } from './push/PushConfig';
import { RuntimeConfig } from './RuntimeConfig';
import { Constants } from '../common/Constants';
import { ConfigUtilError } from '../common/errors/ConfigUtilError';
import { Result } from '../common/InternalTypes';
import { Logger } from '../common/Logger';
import { LoggerTexts } from '../common/LoggerTexts';
import { ScopeCheck } from '../common/ScopeCheck';
import { ConfigIdentifier } from '../common/Types';

export class ConfigUtil {
	public static checkBaseConfiguration(centralConfig: CentralConfig) {
		const logger = this._getLogger();
		this._warnApiUseFalseDeprecation(logger, centralConfig.runtimeConfig);
		this._warnDomElementAndUseApiUsage(logger, centralConfig.runtimeConfig);
		this._warnNoThemingEffect(logger, centralConfig);
	}

	public static checkTimedPushConfigValuesLimits(pushConfig: PushConfig) {
		const logger = this._getLogger();
		if (pushConfig && !ScopeCheck.isTestModeActive()) {
			if (pushConfig.intervalTimerInMinutes < Constants.LIMITS.MIN_RECOMMENDED_INTERVAL_TIMER_IN_MINUTES) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_INTERVAL_TIMER_MIN);
			}
			if (pushConfig.minimumTimeToStartTimerInHrs < Constants.LIMITS.MIN_RECOMMENDED_MIN_TIME_TO_START_TIME_IN_HRS) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_MIN_START_TIMER);
			}
			if (pushConfig.intervalInitialTimedPushInDays < Constants.LIMITS.MIN_RECOMMENDED_INTERVAL_INITIAL_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_INTERVAL_INIT_PUSH);
			}
			if (pushConfig.deviationInitialTimedPushInDays < Constants.LIMITS.MIN_RECOMMENDED_DEVIATION_INITIAL_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_DEVIATION_INIT_PUSH);
			}
			if (pushConfig.intervalNextTimedPushInDays < Constants.LIMITS.MIN_RECOMMENDED_INTERVAL_NEXT_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_INTERVAL_NEXT_PUSH);
			}
			if (pushConfig.deviationNextTimedPushInDays < Constants.LIMITS.MIN_RECOMMENDED_DEVIATION_NEXT_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.TIMED_PUSH_VALUE_BELOW_LIMIT_DEVIATION_NEXT_PUSH);
			}
		}
	}

	public static checkAppPushConfigValuesLimits(pushConfig: PushConfig) {
		const logger = this._getLogger();
		if (!ScopeCheck.isTestModeActive()) {
			if (pushConfig.intervalInitialAppPushInDays < Constants.LIMITS.MIN_RECOMMENDED_INTERVAL_INITIAL_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.APP_PUSH_VALUE_BELOW_LIMIT_INTERVAL_INIT_PUSH);
			}
			if (pushConfig.deviationInitialAppPushInDays < Constants.LIMITS.MIN_RECOMMENDED_DEVIATION_INITIAL_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.APP_PUSH_VALUE_BELOW_LIMIT_DEVIATION_INIT_PUSH);
			}
			if (pushConfig.intervalNextAppPushInDays < Constants.LIMITS.MIN_RECOMMENDED_INTERVAL_NEXT_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.APP_PUSH_VALUE_BELOW_LIMIT_INTERVAL_NEXT_PUSH);
			}
			if (pushConfig.deviationNextAppPushInDays < Constants.LIMITS.MIN_RECOMMENDED_DEVIATION_NEXT_PUSH_IN_DAYS) {
				logger.warn(LoggerTexts.CFG_UTIL.APP_PUSH_VALUE_BELOW_LIMIT_DEVIATION_NEXT_PUSH);
			}
		}
	}

	public static isConfigIdentifierIsValid(configIdentifier: ConfigIdentifier): Result<boolean> {
		if (!configIdentifier.unitId) {
			return { success: false, error: new ConfigUtilError(undefined, LoggerTexts.CFG_UTIL.INIT_CFG_IDENTIFIER_INVALID_UNIT_ID) };
		}
		if (!configIdentifier.environment) {
			return {
				success: false,
				error: new ConfigUtilError(undefined, LoggerTexts.CFG_UTIL.INIT_CFG_IDENTIFIER_INVALID_ENVIRONMENT)
			};
		}
		if (!configIdentifier.configUrl || !this._isValidConfigUrl(configIdentifier.configUrl)) {
			return { success: false, error: new ConfigUtilError(undefined, LoggerTexts.CFG_UTIL.INIT_CFG_IDENTIFIER_INVALID_CFG_URL) };
		}
		return { success: true, result: true };
	}

	private static _getLogger(): Logger {
		return new Logger(Constants.FILE_CFG_UTIL);
	}

	private static _warnDomElementAndUseApiUsage(logger: Logger, runtimeConfig: RuntimeConfig) {
		if (runtimeConfig.useApi === true && runtimeConfig.domElementId) {
			logger.warn(LoggerTexts.CFG_UTIL.RUNTIME_CFG_USEAPI_TRUE_THEN_DOMELEMENT_ID_NO_EFFECT);
		}
	}

	private static _warnNoThemingEffect(logger: Logger, centralConfig: CentralConfig) {
		if (centralConfig.themingConfig) {
			if (!centralConfig.themingConfig.writeToGlobals) {
				logger.warn(LoggerTexts.CFG_UTIL.THEMING_CFG_WRITE_TO_GLOBALS_SET_TO_FALSE_NO_EFFECT);
			}
		}
	}

	private static _warnApiUseFalseDeprecation(logger: Logger, runtimeConfig: RuntimeConfig) {
		// If push-features are enabled and useApi=false then warn about deprecation
		if (!runtimeConfig.useApi) {
			logger.warn(LoggerTexts.CFG_UTIL.USE_API_FALSE_DEPRECATION_WARNING);
		}
	}

	private static _isValidConfigUrl(input: string) {
		let url: URL;
		try {
			url = new URL(input);
		} catch (_) {
			return false;
		}
		return url.protocol === 'https:';
	}
}
